import React, { useState, useEffect } from 'react';
import { Container, Button, ProgressBar, Table, Image, Alert, Spinner, Form } from 'react-bootstrap';
import { db, storage } from '../FireBase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import ToastAlert from '../components/ToastAlert';
import { FaTrash } from 'react-icons/fa6';

const SliderImageControl = () => {
  const [file, setFile] = useState(null);
  const [manualVideoUrl, setManualVideoUrl] = useState('');
  const [positions, setPositions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [mediaList, setMediaList] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [mediaType, setMediaType] = useState('image'); // ✅ image or video

  const mediaCollectionRef = collection(db, 'slider_images');

  // Fetch all media
  useEffect(() => {
    const fetchMedia = async () => {
      setIsLoading(true);
      try {
        const data = await getDocs(mediaCollectionRef);
        setMediaList(data.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
      } catch (err) {
        console.error('Error fetching media: ', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMedia();
  }, []);

  // Handle file input
  const handleFileChange = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
  };

  // Handle checkbox changes
  const handlePositionCheckbox = (e) => {
    const value = e.target.value;
    setPositions((prev) =>
      prev.includes(value)
        ? prev.filter((pos) => pos !== value)
        : [...prev, value]
    );
  };

  // Upload file to Firebase Storage and Firestore
  const handleUpload = () => {
    if (!file && !manualVideoUrl) {
      setToastMessage('Please select a file or enter a video URL.');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    if (positions.length === 0) {
      setToastMessage('Please select at least one position (Top or Bottom).');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    // ✅ Handle manual video URL (no upload)
    if (manualVideoUrl && mediaType === 'video') {
      addDoc(mediaCollectionRef, {
        mediaUrl: manualVideoUrl,
        positions,
        mediaType: 'video'
      })
        .then(async () => {
          const updatedData = await getDocs(mediaCollectionRef);
          setMediaList(updatedData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
          setToastMessage('Video URL added successfully!');
          setToastVariant('success');
          setShowToast(true);
          setManualVideoUrl('');
          setPositions([]);
        })
        .catch((err) => {
          console.error('Error adding video URL:', err);
          setError('Failed to add video URL.');
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // ✅ Handle file upload (image or video)
    const storageRef = ref(storage, `slider_media/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progressPercentage = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
        setProgress(progressPercentage);
      },
      (error) => {
        setError('Upload failed, please try again.');
        console.error('Upload failed', error);
        setIsLoading(false);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          await addDoc(mediaCollectionRef, {
            mediaUrl: downloadURL,
            positions,
            mediaType
          });

          setToastMessage(`${mediaType === 'video' ? 'Video' : 'Image'} uploaded successfully!`);
          setToastVariant('success');
          setShowToast(true);

          // Refresh list
          const updatedData = await getDocs(mediaCollectionRef);
          setMediaList(updatedData.docs.map((doc) => ({ ...doc.data(), id: doc.id })));

          // Reset fields
          setFile(null);
          setPositions([]);
          setProgress(0);
          setManualVideoUrl('');
          document.getElementById('fileInput').value = '';
        } catch (err) {
          setError('Failed to upload file URL to Firestore.');
          console.error('Error storing URL:', err);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  // Delete media
  const handleDelete = async (id, mediaUrl) => {
    setIsLoading(true);
    try {
      if (mediaUrl.startsWith('https://firebasestorage')) {
        const fileRef = ref(storage, mediaUrl);
        await deleteObject(fileRef);
      }

      const mediaDoc = doc(db, 'slider_images', id);
      await deleteDoc(mediaDoc);
      setMediaList(mediaList.filter((m) => m.id !== id));

      setToastMessage('Deleted successfully!');
      setToastVariant('danger');
      setShowToast(true);
    } catch (error) {
      setError('Error deleting item.');
      console.error('Delete failed', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update positions in Firestore
  const handlePositionUpdate = async (id, posArray) => {
    try {
      const docRef = doc(db, 'slider_images', id);
      await updateDoc(docRef, { positions: posArray });
      setMediaList((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, positions: posArray } : m
        )
      );
      setToastMessage('Positions updated!');
      setToastVariant('info');
      setShowToast(true);
    } catch (err) {
      console.error('Error updating positions:', err);
    }
  };

  return (
    <Container className='inventory-page' style={{ padding: '5%' }}>
      <ToastAlert show={showToast} setShow={setShowToast} message={toastMessage} variant={toastVariant} />

      <h2>Slider Media Control</h2>
      <hr />

      {/* Upload Section */}
      <Form.Group className="mb-3">
        <Form.Label>Media Type:</Form.Label>
        <Form.Select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
          <option value="image">Image</option>
          <option value="video">Video</option>
        </Form.Select>
      </Form.Group>

      {mediaType === 'image' ? (
        <Form.Group controlId="formFile" className="mb-3">
          <Form.Label>Select Image:</Form.Label>
          <Form.Control type="file" id="fileInput" accept="image/*" onChange={handleFileChange} />
        </Form.Group>
      ) : (
        <>
          <Form.Group controlId="formVideoFile" className="mb-3">
            <Form.Label>Select Video (optional):</Form.Label>
            <Form.Control type="file" id="fileInput" accept="video/*" onChange={handleFileChange} />
          </Form.Group>
          <Form.Group controlId="manualVideoUrl" className="mb-3">
            <Form.Label>Or Enter Video URL (YouTube / hosted):</Form.Label>
            <Form.Control
              type="text"
              placeholder="https://example.com/video.mp4"
              value={manualVideoUrl}
              onChange={(e) => setManualVideoUrl(e.target.value)}
            />
          </Form.Group>
        </>
      )}

      <Form.Group className="mb-3">
        <Form.Label>Select Position(s):</Form.Label>
        <div>
          <Form.Check inline type="checkbox" label="Top Slider" value="top" checked={positions.includes('top')} onChange={handlePositionCheckbox} />
          <Form.Check inline type="checkbox" label="Bottom Slider" value="bottom" checked={positions.includes('bottom')} onChange={handlePositionCheckbox} />
        </div>
      </Form.Group>

      <Button onClick={handleUpload} disabled={isLoading}>
        {isLoading ? 'Uploading...' : 'Upload'}
      </Button>

      {progress > 0 && <ProgressBar now={progress} label={`${progress}%`} className="mt-3" />}
      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

      <hr />
      <h3>Uploaded Media</h3>

      {isLoading ? (
        <div className="text-center my-5">
          <Spinner animation="grow" style={{ color: '#3f7936' }} />
          <p>Loading...</p>
        </div>
      ) : (
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Preview</th>
              <th>Type</th>
              <th>Positions</th>
              <th>Edit Positions</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
  {mediaList.length > 0 ? (
    mediaList.map((m) => {
      const url = m.mediaUrl || m.imageUrl;              // ✅ Support old & new field
      const type = m.mediaType || m.type || 'image';     // ✅ Support old & new field
      const pos = m.positions || (m.position ? [m.position] : []); // ✅ Support single 'position' field too

      return (
        <tr key={m.id}>
          <td style={{ width: '220px' }}>
            {type === 'image' ? (
              <Image src={url} alt="Slider" thumbnail style={{ width: '200px' }} />
            ) : url.includes('youtube.com') || url.includes('youtu.be') ? (
              // ✅ Show YouTube embed
              <iframe
                width="200"
                height="120"
                src={
                  url.includes('watch?v=')
                    ? url.replace('watch?v=', 'embed/')
                    : url.replace('youtu.be/', 'www.youtube.com/embed/')
                }
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            ) : (
              // ✅ Regular hosted video
              <video width="200" controls>
                <source src={url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </td>

          <td>{type.toUpperCase()}</td>
          <td>{pos.join(', ').toUpperCase() || '—'}</td>
          <td>
            <Form.Check
              inline
              type="checkbox"
              label="Top"
              checked={pos.includes('top')}
              onChange={(e) => {
                const newPos = e.target.checked
                  ? [...pos, 'top']
                  : pos.filter((p) => p !== 'top');
                handlePositionUpdate(m.id, newPos);
              }}
            />
            <Form.Check
              inline
              type="checkbox"
              label="Bottom"
              checked={pos.includes('bottom')}
              onChange={(e) => {
                const newPos = e.target.checked
                  ? [...pos, 'bottom']
                  : pos.filter((p) => p !== 'bottom');
                handlePositionUpdate(m.id, newPos);
              }}
            />
          </td>
          <td>
            <Button variant="outline-danger" onClick={() => handleDelete(m.id, url)} disabled={isLoading}>
              <FaTrash />
            </Button>
          </td>
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan="5" className="text-center">
        No media found.
      </td>
    </tr>
  )}
</tbody>

        </Table>
      )}
    </Container>
  );
};

export default SliderImageControl;
